/**
 * Centralized error handling service for the frontend application.
 */

class ErrorHandler {
  constructor() {
    this.errorListeners = [];
    this.userFriendlyMessages = new Map([
      // Authentication errors
      ['AUTHENTICATION_ERROR', 'Please sign in to continue'],
      ['AUTHORIZATION_ERROR', 'You do not have permission to perform this action'],
      ['INVALID_CREDENTIALS', 'Invalid email or password'],
      ['TOKEN_EXPIRED', 'Your session has expired. Please sign in again'],

      // Validation errors
      ['VALIDATION_ERROR', 'Please check your input and try again'],
      ['EMAIL_ALREADY_EXISTS', 'This email address is already registered'],
      ['USERNAME_ALREADY_EXISTS', 'This username is already taken'],
      ['INVALID_EMAIL_FORMAT', 'Please enter a valid email address'],
      [
        'PASSWORD_TOO_WEAK',
        'Password must be at least 8 characters with uppercase, lowercase, and numbers',
      ],

      // Network errors
      ['NETWORK_ERROR', 'Network connection error. Please check your internet connection'],
      ['SERVER_ERROR', 'Server error occurred. Please try again later'],
      ['TIMEOUT_ERROR', 'Request timed out. Please try again'],

      // Resource errors
      ['NOT_FOUND', 'The requested item was not found'],
      ['RESOURCE_UNAVAILABLE', 'This item is currently unavailable'],
      ['OUT_OF_STOCK', 'This item is out of stock'],

      // Rate limiting
      ['RATE_LIMIT_EXCEEDED', 'Too many requests. Please wait a moment and try again'],

      // Generic errors
      ['UNKNOWN_ERROR', 'An unexpected error occurred. Please try again'],
      ['MAINTENANCE_MODE', 'The site is currently under maintenance. Please try again later'],
    ]);
  }

  /**
   * Add an error listener
   * @param {Function} listener - Error listener function
   */
  addErrorListener(listener) {
    this.errorListeners.push(listener);
  }

  /**
   * Remove an error listener
   * @param {Function} listener - Error listener function to remove
   */
  removeErrorListener(listener) {
    this.errorListeners = this.errorListeners.filter(l => l !== listener);
  }

  /**
   * Notify all error listeners
   * @param {Error} error - Error object
   * @param {string} context - Error context
   */
  notifyListeners(error, context) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error, context);
      } catch (listenerError) {
        console.error('Error in error listener:', listenerError);
      }
    });
  }

  /**
   * Handle API errors from HTTP responses
   * @param {Object} error - Axios error object
   * @param {string} context - Context where error occurred
   */
  handleApiError(error, context = 'api') {
    const processedError = {
      type: 'api',
      context,
      timestamp: new Date().toISOString(),
      requestId: null,
      userMessage: null,
      technicalMessage: null,
      statusCode: null,
      retry: false,
    };

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      processedError.statusCode = status;

      if (data && data.error) {
        processedError.code = data.error.code;
        processedError.technicalMessage = data.error.message;
        processedError.requestId = data.error.request_id;
        processedError.details = data.error.details;
      }
      if (data && data.message) {
        processedError.serverMessage = data.message;
      }

      // Determine if retry is appropriate
      processedError.retry = this.shouldRetry(status);
    } else if (error.request) {
      // Network error
      processedError.code = 'NETWORK_ERROR';
      processedError.technicalMessage = 'No response received from server';
      processedError.retry = true;
    } else {
      // Request setup error
      processedError.code = 'REQUEST_ERROR';
      processedError.technicalMessage = error.message;
      processedError.retry = false;
    }

    processedError.userMessage =
      processedError.serverMessage || this.getUserFriendlyMessage(processedError.code);

    // Log the error
    this.logError(processedError);

    // Notify listeners
    this.notifyListeners(processedError, context);

    return processedError;
  }

  /**
   * Handle validation errors
   * @param {Object} validationErrors - Field validation errors
   * @param {string} context - Context where validation failed
   */
  handleValidationError(validationErrors, context = 'validation') {
    const processedError = {
      type: 'validation',
      context,
      timestamp: new Date().toISOString(),
      code: 'VALIDATION_ERROR',
      fieldErrors: validationErrors,
      userMessage: 'Please correct the errors below',
      retry: false,
    };

    this.logError(processedError);
    this.notifyListeners(processedError, context);

    return processedError;
  }

  /**
   * Handle JavaScript errors
   * @param {Error} error - JavaScript error
   * @param {string} context - Context where error occurred
   */
  handleJavaScriptError(error, context = 'javascript') {
    const processedError = {
      type: 'javascript',
      context,
      timestamp: new Date().toISOString(),
      code: 'JAVASCRIPT_ERROR',
      technicalMessage: error.message,
      stack: error.stack,
      userMessage: 'An unexpected error occurred. Please refresh the page and try again',
      retry: false,
    };

    this.logError(processedError);
    this.notifyListeners(processedError, context);

    return processedError;
  }

  /**
   * Get user-friendly error message
   * @param {string} errorCode - Error code
   * @returns {string} User-friendly message
   */
  getUserFriendlyMessage(errorCode) {
    return (
      this.userFriendlyMessages.get(errorCode) || this.userFriendlyMessages.get('UNKNOWN_ERROR')
    );
  }

  /**
   * Show user-friendly error message (can be overridden by UI framework)
   * @param {Object} error - Processed error object
   */
  showUserFriendlyMessage(error) {
    // Try to use toast notifications if available
    if (window.showToast) {
      window.showToast(error.userMessage, {
        type: 'error',
        title: 'Error',
        duration: 7000,
      });
    } else {
      // Fallback to console warning
      console.warn('Error occurred:', error.userMessage);
    }

    // For development, also show technical details
    if (process.env.NODE_ENV === 'development') {
      console.error('Technical details:', error);
    }
  }

  /**
   * Determine if an error should trigger a retry
   * @param {number} statusCode - HTTP status code
   * @returns {boolean} Whether retry is appropriate
   */
  shouldRetry(statusCode) {
    // Retry for server errors and rate limiting
    return [500, 502, 503, 504, 429].includes(statusCode);
  }

  /**
   * Log error for debugging and monitoring
   * @param {Object} error - Processed error object
   */
  logError(error) {
    const logData = {
      timestamp: error.timestamp,
      type: error.type,
      context: error.context,
      code: error.code,
      message: error.technicalMessage,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId(),
    };

    // Add additional details based on error type
    if (error.requestId) {
      logData.requestId = error.requestId;
    }

    if (error.statusCode) {
      logData.statusCode = error.statusCode;
    }

    if (error.stack) {
      logData.stack = error.stack;
    }

    // In development, log to console
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 Error: ${error.code}`);
      console.error('User Message:', error.userMessage);
      console.error('Technical Message:', error.technicalMessage);
      console.error('Context:', error.context);
      console.error('Full Details:', logData);
      console.groupEnd();
    }

    // TODO: Send to error monitoring service (e.g., Sentry, LogRocket)
    this.sendToMonitoringService(logData);
  }

  /**
   * Get current user ID for error tracking
   * @returns {string|null} User ID or null
   */
  getCurrentUserId() {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.sub;
      }
    } catch (error) {
      // Ignore token parsing errors
    }
    return null;
  }

  /**
   * Get session ID for error tracking
   * @returns {string} Session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Send error data to monitoring service
   * @param {Object} logData - Error log data
   */
  sendToMonitoringService(logData) {
    // TODO: Implement integration with error monitoring service
    // This could send to Sentry, LogRocket, or custom logging endpoint

    // For now, just store in localStorage for development
    try {
      const errors = JSON.parse(localStorage.getItem('errorLogs') || '[]');
      errors.push(logData);

      // Keep only last 100 errors
      if (errors.length > 100) {
        errors.splice(0, errors.length - 100);
      }

      localStorage.setItem('errorLogs', JSON.stringify(errors));
    } catch (error) {
      console.warn('Failed to store error log:', error);
    }
  }

  /**
   * Clear stored error logs (for development/testing)
   */
  clearErrorLogs() {
    localStorage.removeItem('errorLogs');
  }

  /**
   * Get stored error logs (for development/testing)
   * @returns {Array} Array of error logs
   */
  getErrorLogs() {
    try {
      return JSON.parse(localStorage.getItem('errorLogs') || '[]');
    } catch (error) {
      return [];
    }
  }
}

// Create singleton instance
const errorHandler = new ErrorHandler();

// Set up global error handlers
window.addEventListener('error', event => {
  errorHandler.handleJavaScriptError(event.error, 'global');
});

window.addEventListener('unhandledrejection', event => {
  const error = new Error(event.reason);
  errorHandler.handleJavaScriptError(error, 'promise');
});

export default errorHandler;
